import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ModalService } from '@commons/core/ui/modal/modal.service';
import { Audio, Playlist } from '@commons/toolbox/media-player/models/audio';
import { ChannelUtil } from '@commons/util/helper/channel';
import { HistoryCsrCacheService } from '@history/core/cache/history-csr-cache.service';
import {
  HistoryInteractionFilter,
  HistoryInteractionSource,
  RecordStatusCache,
} from '@history/core/cache/history-interaction-source.service';
import { EXTERNAL_ROUTING_NAME, RESPONSE_AUTO_UUID } from '@history/core/history-deprecated.service';
import { map, of } from 'rxjs';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import * as moment from 'moment';

import {
  capitalize,
  Civility,
  CngTableDefBuilder,
  ContactEntryDoc,
  CustomerFacade,
  joiner,
  MomentService,
  Option,
  ProfileService,
  RecordService,
  rowOfAction,
  TranslateService,
  UntilDestroy,
  untilDestroyed,
} from '@contact/upgrade';

enum TableAction {
  EDIT = 'EDIT',
  CONSULT = 'CONSULT',
  READ = 'READ',
}

enum FormError {
  BEGIN_DATE_AFTER_END_DATE = 'begin_date_should_be_prior_to_end_date',
}
const RECORD_DONWLOAD_END_POINT = 'downloadRecord';
const RECORD_STREAM_END_POINT = 'getRecordStream';

@UntilDestroy()
@Component({
  selector: 'cng-interaction-contact-view',
  templateUrl: 'interaction-view.component.html',
  styleUrls: ['./interaction-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryInteractionViewComponent {
  TableAction = TableAction;
  Filter = HistoryInteractionFilter;

  playlist: Playlist;
  currentAudio: Audio;
  urlListenRespMessage: string;

  checkDates = (group: FormGroup) => {
    let beginDate = group.get(HistoryInteractionFilter.BEGIN_DATE)?.value;
    let endDate = group.get(HistoryInteractionFilter.END_DATE)?.value;

    beginDate = moment.isMoment(beginDate) ? beginDate : moment(beginDate);
    endDate = moment.isMoment(endDate) ? endDate : moment(endDate);

    if (beginDate.isValid() && endDate.isValid()) {
      return beginDate.isSameOrBefore(endDate) ? null : { [FormError.BEGIN_DATE_AFTER_END_DATE]: true };
    }
    return null;
  };

  readonly searchForm = this.fb.group({
    [HistoryInteractionFilter.CSR]: new FormControl(undefined),
    [HistoryInteractionFilter.FIRSTNAME]: new FormControl(undefined),
    [HistoryInteractionFilter.LASTNAME]: new FormControl(undefined),
    [HistoryInteractionFilter.COMPANY_NAME]: new FormControl(undefined),
    [HistoryInteractionFilter.URI]: new FormControl(undefined),
    [HistoryInteractionFilter.CAMPAIGN]: new FormControl([]),
    [HistoryInteractionFilter.CHANNEL]: new FormControl([]),
    [HistoryInteractionFilter.BEGIN_DATE]: [undefined],
    [HistoryInteractionFilter.END_DATE]: [undefined],
  },{ validators: this.checkDates });
  // table definition
  tableDef = this.tdb
    .from(this)
    .setPageOptions([10, 20, 50])
    .setPageSize(10)
    .setFilterExpanded(false)
    .setFilterCanExpand(true)
    .setFilterAutoFill(false)
    .setForm(this.searchForm)
    .paramStored('HistoryInteractionViewTable', this.historyInteractionSource)
    .build();

  recordStatusCache$ = this.historyInteractionSource.recordStatusCache$;

  channels$ = of([
    'PHONE_INCOMING',
    'PHONE_OUTGOING',
    'UNITARY_PHONE_OUTGOING',
    'MAIL_INCOMING',
    'MAIL_OUTGOING',
    'CHAT_IADVIZE_INCOMING',
    'CHAT_INCOMING',
    'SOCIAL_NETWORK_INCOMING',
    'SURVEY_OUTGOING',
    'VISIO_OUTGOING',
    'SMS_OUTGOING',
  ]).pipe(
    map(channels =>
      channels
        .map(channel => ({
          value: channel,
          label: this.translateService.instant(`commons:basic.channels.${channel}`),
        }))
        .sort((a, b) => this.compareIgnoreCase(a.label, b.label))
    )
  );

  campaigns$ = of(['NORMAL', 'CALLBACK', 'SCHEDULED_CALL_BACK']).pipe(
    map(campaigns =>
      campaigns
        .map(campaign => ({
          value: campaign,
          label: this.translateService.instant(`commons:basic.campaignType.${campaign}`),
        }))
        .sort((a, b) => this.compareIgnoreCase(a.label, b.label))
    )
  );

  csrs$ = this.historyCsrCacheService.get({ force: true }).pipe(
    map(csrs =>
      csrs
        .map(({ uuid, firstName, lastName }) => ({
          value: uuid,
          label: `${capitalize(firstName)} ${capitalize(lastName)}`,
        }))
        .sort((a, b) => this.compareIgnoreCase(a.label, b.label))
    )
  );

  constructor(
    private readonly tdb: CngTableDefBuilder,
    private readonly historyInteractionSource: HistoryInteractionSource,
    private readonly momentService: MomentService,
    private readonly customerFacade: CustomerFacade,
    private readonly modalService: ModalService,
    private readonly translateService: TranslateService,
    private readonly historyCsrCacheService: HistoryCsrCacheService,
    private readonly recordService: RecordService,
    private readonly profileService: ProfileService,
    private readonly fb: FormBuilder
  ) {
    this.recordService.getRecordStreamUrl().subscribe(url => (this.urlListenRespMessage = url));

    /**combineLatest([this.appConf, this.authService.getSessionIdFromCookie()]).subscribe(([{ responderServletUrl }, sessionId]) => {
      const availableResponderServletUrl = responderServletUrl?.endsWith('/') ? responderServletUrl : `${responderServletUrl}/`;
      this.recordDeprecatedService.getRecordStreamUrl().subscribe(url => this.urlListenRespMessage = url);
      this.urlListenRespMessage = `${availableResponderServletUrl.replace(
        RECORD_DONWLOAD_END_POINT,
        RECORD_STREAM_END_POINT
      )}${sessionId}/`;
    });*/
  }


  ngOnInit() {
    this.searchForm.setValidators(this.checkDates);
    }

  ngAfterViewInit() {
    // columns definition
    this.tableDef.columnDefs = [
      {
        key: 'entryDate',
        header: 'commons:basic.dateTime',
        cell: ({ entryDate }) => entryDate && this.momentService.readableDateTimeNoSep(entryDate),
        sort: true,
      },
      {
        key: 'civility',
        header: 'commons:basic.civility',
        cell: ({ civility }) => (civility && civility !== Civility.NOTSET ? `commons:basic.civility_${civility.toLowerCase()}` : ''),
        sort: true,
      },
      {
        key: 'contactFirstName',
        header: 'commons:basic.firstname',
        cell: ({ contactFirstName }) => capitalize(contactFirstName),
        sort: true,
      },
      {
        key: 'contactLastName',
        header: 'commons:basic.lastname',
        cell: ({ contactLastName }) => capitalize(contactLastName),
        sort: true,
      },
      {
        key: 'contactUri',
        header: 'commons:popin.customer_card.address',
        sort: true,
      },
      {
        key: 'contactCompanyName',
        header: 'commons:popin.customer_card.company_name',
        cell: ({ contactCompanyName }) => capitalize(contactCompanyName),
        sort: true,
      },
      {
        key: 'canalName',
        header: 'commons:basic.channelName',
        cell: ({ canalName, canalDirection }) =>
          this.translateService.instant(ChannelUtil.toLabel(ChannelUtil.from(canalName, canalDirection))),
        sort: true,
      },
      {
        key: 'csrLogin',
        header: 'commons:basic.csrName',
        cell: ({ csrLogin }) => this.computeCsrLogin(csrLogin),
        sort: true,
      },
      {
        key: 'skills',
        header: 'commons:basic.skillList',
        cell: ({ skills }) =>
          skills && skills.length > 0
            ? joiner(
              skills.map(({ name }) => name),
              ', '
            )
            : '-',
        sort: false,
      },
    ];
    // actions behavior
    const actions$ = this.tableDef.actions$;
    // Edit
    actions$
      .pipe(rowOfAction(TableAction.EDIT), untilDestroyed(this))
      .subscribe(({ contactUuid }) => this.customerFacade.updateDialog(contactUuid));
    // Consult
    actions$
      .pipe(rowOfAction(TableAction.CONSULT), untilDestroyed(this))
      .subscribe(({ uuid, contactUuid, contactFirstName, contactLastName, canalName, canalDirection, contactUri }) =>
        // TODO replace by Anguler histo popin
        this.modalService.open({
          keyboard: false,
          backdrop: false,
          fullscreen: true,
          ctrlKeyboard: true,
          component: 'contactContactViewHistoPopin',
          modalId: 'histoModalContact_' + uuid,
          title:
            this.translateService.instant('agent:contact.info.popin.title') +
            `${capitalize(contactLastName)} ${capitalize(contactFirstName)}`,
          resolve: {
            selectContactEntry: () => ({
              id: uuid,
              contactEntry: {
                contactUuid,
                contactUri,
                canalName,
                canalDirection,
                contactFirstName,
                contactLastName,
              },
            }),
            checkVisibilityMail: () => Option.of(false),
            inProcessActionsAllowed: () => Option.of(false),
          },
        })
      );
    // Read
    actions$.pipe(rowOfAction(TableAction.READ), untilDestroyed(this)).subscribe(contactEntry => this.playRecord(contactEntry));
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @angular-eslint/no-empty-lifecycle-method
  ngOnDestroy() {}

  hasNotRecord(cache: RecordStatusCache, entry: ContactEntryDoc) {
    return (
      !cache[entry.uuid] ||
      (cache[entry.uuid] !== 'AVAILABLE' && cache[entry.uuid] !== 'OUT_STRATEGY' && cache[entry.uuid] !== 'RESTRICTED')
    );
  }

  isRecordRestricted(cache: RecordStatusCache, entry: ContactEntryDoc) {
    return cache[entry.uuid] && cache[entry.uuid] === 'RESTRICTED';
  }

  playRecord(entry: ContactEntryDoc): void {
    this.currentAudio = this.getCurrentAudioStream(entry);
  }

  // stream record
  getCurrentAudioStream(entry: ContactEntryDoc): Audio {
    return {
      title: `[${entry.contactUri}]`,
      url: this.getListenRecordURI(entry),
    };
  }

  getListenRecordURI(entry: ContactEntryDoc): string {
    return `${this.urlListenRespMessage}${encodeURIComponent(entry.technicalId)}`;
  }

  onChangeAudio(audio: Audio): void {
    this.currentAudio = audio;
  }

  computeCsrLogin(csrLogin: string | undefined): string {
    if (csrLogin && csrLogin.indexOf(RESPONSE_AUTO_UUID) !== -1) {
      return this.translateService.instant('history:viewInteractions.RESPONSE_AUTO');
    }

    if (csrLogin && csrLogin.indexOf(EXTERNAL_ROUTING_NAME) !== -1) {
      return this.translateService.instant('history:viewInteractions.' + EXTERNAL_ROUTING_NAME);
    }

    return csrLogin || '-';
  }

  private compareIgnoreCase(a: string, b: string): number {
    if (a == null && b == null) {
      return 0;
    }
    if (a == null) {
      return -1;
    }
    if (b == null) {
      return 1;
    }
    const la: string = a.toLowerCase();
    const lb: string = b.toLowerCase();
    return la === lb ? 0 : la < lb ? -1 : 1;
  }
}
